import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import config from '../config/api';
import { eraseCookie, setCookie } from '../utils/cookies';
import { useAuthStore } from '../stores/auth-store';
import { useUserStore } from '../stores/user-store';
import { useOrganizationStore } from '../stores/organization-store';
import { onTokenRefreshed } from '../mobile-bridge';

// Storage keys
export const TOKEN_KEY = 'auth_token';
export const REFRESH_TOKEN_KEY = 'auth_refresh_token';
export const TOKEN_EXPIRES_AT_KEY = 'auth_token_expires_at';

// Token refresh state
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Core token refresh logic — calls the refresh API directly (raw axios, no interceptor)
 * and persists the new tokens to localStorage, cookies, and axios defaults.
 * Returns the new tokens or throws on failure.
 */
interface RefreshResult {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
}

const performTokenRefresh = async (currentRefreshToken: string): Promise<RefreshResult> => {
  const response = await axios.post(
    `${config.API_BASE_URL}/api/v1/auth/refresh`,
    { refreshToken: currentRefreshToken },
    { headers: { 'Content-Type': 'application/json' } }
  );

  const newAccessToken = response.data.data?.auth?.accessToken;
  const newRefreshToken = response.data.data?.auth?.refreshToken;
  const expiresIn = response.data.data?.auth?.expiresIn;

  if (!newAccessToken) {
    throw new Error('No access token in refresh response');
  }

  // Save tokens to localStorage
  localStorage.setItem(TOKEN_KEY, newAccessToken);
  setCookie(TOKEN_KEY, newAccessToken, 7);
  if (newRefreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
    setCookie(REFRESH_TOKEN_KEY, newRefreshToken, 7);
  }

  // Save expiration
  const expiresAtMs = expiresIn
    ? Date.now() + (expiresIn * 1000)
    : Date.now() + 24 * 60 * 60 * 1000;
  localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expiresAtMs.toString());

  // Update axios default header
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

  // Notify mobile bridge
  onTokenRefreshed({
    accessToken: newAccessToken,
    refreshToken: newRefreshToken || '',
    expiresIn,
  });

  return { accessToken: newAccessToken, refreshToken: newRefreshToken || '', expiresIn };
};

// Helper to clear auth data and all stores
export const clearData = () => {
  // Clear localStorage
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);

  // Clear cookies for middleware
  eraseCookie(TOKEN_KEY);
  eraseCookie(REFRESH_TOKEN_KEY);

  // Clear all Zustand stores
  try {
    const { clearAuth } = useAuthStore.getState();
    const { clearUser } = useUserStore.getState();
    const { clearOrganization } = useOrganizationStore.getState();

    clearAuth();
    clearUser();
    clearOrganization();
  } catch (error) {
    console.error('Error clearing stores:', error);
  }

  // Clear all localStorage (catches any other persisted data)
  localStorage.clear();
};

// Helper to check if token is expired or about to expire
export const isTokenExpiringSoon = (bufferMinutes = 5): boolean => {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);
  if (!expiresAt) return true;

  const expirationTime = parseInt(expiresAt, 10);
  const currentTime = Date.now();
  const bufferTime = bufferMinutes * 60 * 1000;

  return expirationTime - currentTime < bufferTime;
};

// Helper to check if user is authenticated
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem(TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  return !!(token && refreshToken);
};

export const axiosInstance = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor with proactive token refresh
axiosInstance.interceptors.request.use(
  async (config) => {
    // Skip token refresh for auth endpoints
    if (config.url?.includes('/auth/')) {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    }

    // Check if token is expiring soon and refresh proactively
    if (isTokenExpiringSoon() && !isRefreshing) {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (refreshToken) {
        try {
          await refreshAccessToken();
        } catch (error) {
          // If refresh fails, let the response interceptor handle it
          console.warn('Proactive token refresh failed:', error);
        }
      }
    }

    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);


// Helper to check if error is authentication-related
const isAuthError = (error: AxiosError): boolean => {

  const responseDataError: any = error.response?.data;
  const responeDataStatus = responseDataError.error.statusCode
  const responeDataCode = responseDataError.error.code

  // Check for 401 Unauthorized
  if (responeDataStatus === 401) return true;

  // Check for 403 with INVALID_AUTHENTICATION code
  if (responeDataStatus === 403 && (responeDataCode === 'INVALID_AUTHENTICATION' || responeDataCode === 'MISSING_AUTHENTICATION')) {
    return true;
  }

  return false;
};

// Response interceptor with token refresh logic
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Don't retry for refresh endpoint itself to avoid infinite loop
    if (originalRequest?.url?.includes('/auth/refresh')) {
      clearData();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Handle authentication errors (401 or 403 with INVALID_AUTHENTICATION)
    if (isAuthError(error) && !originalRequest?._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            }
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        processQueue(new Error('No refresh token available'), null);
        isRefreshing = false;
        clearData();
        if (typeof window !== 'undefined') {
          // window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      try {
        const result = await performTokenRefresh(refreshToken);

        // Process queued requests
        processQueue(null, result.accessToken);

        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Clear auth data on refresh failure
        clearData();

        processQueue(refreshError, null);

        // Redirect to login
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Helper to manually trigger token refresh (for proactive refresh)
export const refreshAccessToken = async (): Promise<boolean> => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return false;
  }

  try {
    await performTokenRefresh(refreshToken);
    return true;
  } catch (error) {
    // Clear auth data on failure
    clearData();
    return false;
  }
};


// Helper function to extract error message from API response
export const extractErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    // Check if it's an Axios error with response data
    const axiosError = error as any;
    if (axiosError && axiosError.response?.data?.error?.message) {
      return axiosError.response.data.error.message;
    }
    if (axiosError && axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }

    if (axiosError && axiosError.response?.data?.details && axiosError.response?.data?.details.length > 0 && axiosError.response?.data?.details[0].message) {
      return axiosError.response.data.details[0].message;
    }

    if (axiosError && axiosError.response?.data?.error?.detail && axiosError.response?.data?.error?.detail.message) {
      return axiosError.response.data.error.detail.message;
    }

    // Try to parse error message as JSON
    try {
      const errorResponse = JSON.parse(error.message);
      if (errorResponse.error?.message) {
        return errorResponse.error.message;
      }
    } catch {
      // If parsing fails, return the original message
    }

    return error.message;
  }
  return 'An unknown error occurred.';
};